import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../lib/api-client';
import { queryKeys } from '../lib/query-client';

/**
 * User Profile Types
 */
export interface UserProfile {
  id: string;
  email: string;
  name?: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
  // Demographic fields
  preferredName?: string;
  familyName?: string;
  dateOfBirth?: string;
  heightCm?: number;
  sexAssignedAtBirth?: 'male' | 'female' | 'prefer-not-to-say';
  measurementSystem?: 'metric' | 'imperial';
  metadata?: {
    onboardingCompleted?: boolean;
    [key: string]: unknown;
  };
}

export interface UpdateProfileInput {
  name?: string;
  email?: string;
  preferredName?: string;
  familyName?: string;
  dateOfBirth?: string;
  heightCm?: number;
  sexAssignedAtBirth?: 'male' | 'female' | 'prefer-not-to-say';
  measurementSystem?: 'metric' | 'imperial';
  metadata?: {
    onboardingCompleted?: boolean;
    [key: string]: unknown;
  };
}

/**
 * useProfile Hook
 *
 * Fetches and manages the current user's profile data.
 * Uses TanStack Query for caching and background refetching.
 *
 * @example
 * ```tsx
 * function ProfilePage() {
 *   const { data: profile, isLoading, error } = useProfile();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <div>Welcome {profile.name}</div>;
 * }
 * ```
 */
export function useProfile() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: queryKeys.users.me,
    queryFn: () => authenticatedFetch<UserProfile>('/v1/users/me'),
    // Only fetch if user is authenticated
    enabled: isAuthenticated,
    // Keep profile data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * useUpdateProfile Hook
 *
 * Mutation hook for updating the current user's profile.
 * Automatically invalidates and refetches profile data on success.
 *
 * @example
 * ```tsx
 * function EditProfileForm() {
 *   const updateProfile = useUpdateProfile();
 *
 *   async function handleSubmit(data: UpdateProfileInput) {
 *     await updateProfile.mutateAsync(data);
 *     toast.success('Profile updated!');
 *   }
 *
 *   return (
 *     <button
 *       onClick={() => handleSubmit({ name: 'New Name' })}
 *       disabled={updateProfile.isPending}
 *     >
 *       Save
 *     </button>
 *   );
 * }
 * ```
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileInput) =>
      authenticatedFetch<UserProfile>('/v1/users/me', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (updatedProfile) => {
      // Update the cache with the new profile data
      queryClient.setQueryData(queryKeys.users.me, updatedProfile);

      // Or invalidate to trigger a refetch
      // queryClient.invalidateQueries({ queryKey: queryKeys.users.me });
    },
  });
}
